import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Icon } from '@/components/atoms';
import { Modal } from '@/components/molecules';
import { Input } from '@kisanlink/ui-commons';
import { useCreateGRNMutation } from '@/hooks/useErpApi';
import { useAppDispatch } from '@/hooks';
import { addGRN } from '@/features/grns';
import { updatePurchaseOrder } from '@/features/purchaseOrders';
import type { PurchaseOrder } from '@/schemas/purchaseOrders';
import type { GRN, CreateGRNItem } from '@/schemas/grn';

interface DeliveryModalProps {
  purchaseOrder: PurchaseOrder;
  open: boolean;
  onClose: () => void;
  onSuccess: (grn: GRN) => void;
}

type DeliveryPattern = 'accept-all' | 'simple' | 'detailed';

export const DeliveryModal: React.FC<DeliveryModalProps> = ({
  purchaseOrder,
  open,
  onClose,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { mutateAsync: createGRN, isLoading } = useCreateGRNMutation();

  const [error, setError] = useState<string | null>(null);
  const [deliveryPattern, setDeliveryPattern] = useState<DeliveryPattern>('accept-all');

  // Header fields
  const [grnNumber, setGrnNumber] = useState('');
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().split('T')[0]);
  const [receivedBy, setReceivedBy] = useState('');
  const [qualityNotes, setQualityNotes] = useState('');

  // Pattern 1: Accept All
  const [defaultExpiryDate, setDefaultExpiryDate] = useState('');

  // Pattern 2: Simple Accept/Reject
  const [simpleItems, setSimpleItems] = useState(
    purchaseOrder.items.map((item) => ({
      po_item_id: item.id,
      accept: true,
      expiry_date: '',
      rejection_reason: '',
    }))
  );

  // Pattern 3: Detailed Quantities
  const [detailedItems, setDetailedItems] = useState(
    purchaseOrder.items.map((item) => ({
      po_item_id: item.id,
      received_quantity: item.quantity,
      accepted_quantity: item.quantity,
      expiry_date: '',
      rejection_reason: '',
    }))
  );

  // Combined loading state for GRN creation
  const isBusy = isLoading;

  /**
   * Ensures the purchase order status is "verified" before creating GRN
   * Status should already be verified when modal opens, but we verify it here as a safety check
   */
  const ensurePODelivered = async (): Promise<void> => {
    console.log('[DeliveryModal] ensurePODelivered - START');
    console.log('[DeliveryModal] Current PO status:', purchaseOrder.status);

    // If already verified, nothing to do
    if (purchaseOrder.status === 'verified') {
      console.log('[DeliveryModal] PO already verified, proceeding with GRN creation');
      return;
    }

    // If not verified, throw error (status should be updated before opening modal)
    console.error('[DeliveryModal] PO status is not verified:', purchaseOrder.status);
        throw new Error(
      `Purchase order must be in 'verified' status to create GRN. Current status: "${purchaseOrder.status}". Please verify the order first.`
      );
  };

  const handleAcceptAllSubmit = async () => {
    if (!grnNumber.trim()) {
      setError('GRN number is required');
      return;
    }
    if (!receivedBy.trim()) {
      setError('Received by is required');
      return;
    }
    if (!defaultExpiryDate) {
      setError('Default expiry date is required');
      return;
    }

    try {
      setError(null);

      console.log('[DeliveryModal] Calling ensurePODelivered...');
      // Ensure PO status is "delivered" before creating GRN
      await ensurePODelivered();
      console.log('[DeliveryModal] ensurePODelivered completed successfully');

      const items: CreateGRNItem[] = purchaseOrder.items.map((item) => ({
        po_item_id: item.id,
        received_quantity: item.quantity,
        accepted_quantity: item.quantity,
        rejected_quantity: 0,
        expiry_date: defaultExpiryDate,
      }));

      const result = await createGRN({
        po_id: purchaseOrder.id,
        grn_number: grnNumber,
        received_date: receivedDate,
        received_by: receivedBy,
        quality_status: 'accepted',
        remarks: qualityNotes || undefined,
        items,
      });

      dispatch(addGRN(result as never));
      dispatch(
        updatePurchaseOrder({
          ...purchaseOrder,
          status: 'delivered',
          actual_delivery_date: receivedDate,
        } as never)
      );
      onSuccess(result as GRN);
      navigate(`/grns/${(result as GRN).id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create GRN');
    }
  };

  const handleSimpleSubmit = async () => {
    if (!grnNumber.trim()) {
      setError('GRN number is required');
      return;
    }
    if (!receivedBy.trim()) {
      setError('Received by is required');
      return;
    }

    const acceptedItems = simpleItems.filter((item) => item.accept);
    const rejectedItems = simpleItems.filter((item) => !item.accept);

    if (acceptedItems.length === 0 && rejectedItems.length === 0) {
      setError('At least one item must be processed');
      return;
    }

    const hasExpiryErrors = acceptedItems.some((item) => !item.expiry_date);
    if (hasExpiryErrors) {
      setError('Expiry date is required for all accepted items');
      return;
    }

    const hasRejectionErrors = rejectedItems.some((item) => !item.rejection_reason.trim());
    if (hasRejectionErrors) {
      setError('Rejection reason is required for all rejected items');
      return;
    }

    try {
      setError(null);

      console.log('[DeliveryModal] Calling ensurePODelivered...');
      // Ensure PO status is "delivered" before creating GRN
      await ensurePODelivered();
      console.log('[DeliveryModal] ensurePODelivered completed successfully');

      const items: CreateGRNItem[] = simpleItems.map((simpleItem) => {
        const poItem = purchaseOrder.items.find((i) => i.id === simpleItem.po_item_id)!;
        return {
          po_item_id: simpleItem.po_item_id,
          received_quantity: poItem.quantity,
          accepted_quantity: simpleItem.accept ? poItem.quantity : 0,
          rejected_quantity: simpleItem.accept ? 0 : poItem.quantity,
          expiry_date: simpleItem.expiry_date,
        };
      });

      const qualityStatus =
        acceptedItems.length === simpleItems.length
          ? 'accepted'
          : rejectedItems.length === simpleItems.length
            ? 'rejected'
            : 'partial';

      const result = await createGRN({
        po_id: purchaseOrder.id,
        grn_number: grnNumber,
        received_date: receivedDate,
        received_by: receivedBy,
        quality_status: qualityStatus,
        remarks: qualityNotes || undefined,
        items,
      });

      dispatch(addGRN(result as never));
      dispatch(
        updatePurchaseOrder({
          ...purchaseOrder,
          status: 'delivered',
          actual_delivery_date: receivedDate,
        } as never)
      );
      onSuccess(result as GRN);
      navigate(`/grns/${(result as GRN).id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create GRN');
    }
  };

  const handleDetailedSubmit = async () => {
    if (!grnNumber.trim()) {
      setError('GRN number is required');
      return;
    }
    if (!receivedBy.trim()) {
      setError('Received by is required');
      return;
    }

    const hasExpiryErrors = detailedItems.some(
      (item) => item.accepted_quantity > 0 && !item.expiry_date
    );
    if (hasExpiryErrors) {
      setError('Expiry date is required for items with accepted quantity');
      return;
    }

    const hasQuantityErrors = detailedItems.some(
      (item) => item.accepted_quantity > item.received_quantity
    );
    if (hasQuantityErrors) {
      setError('Accepted quantity cannot exceed received quantity');
      return;
    }

    const hasRejectionErrors = detailedItems.some(
      (item) => item.received_quantity - item.accepted_quantity > 0 && !item.rejection_reason.trim()
    );
    if (hasRejectionErrors) {
      setError('Rejection reason is required when accepted < received');
      return;
    }

    try {
      setError(null);

      console.log('[DeliveryModal] Calling ensurePODelivered...');
      // Ensure PO status is "delivered" before creating GRN
      await ensurePODelivered();
      console.log('[DeliveryModal] ensurePODelivered completed successfully');

      const items: CreateGRNItem[] = detailedItems.map((item) => ({
        po_item_id: item.po_item_id,
        received_quantity: item.received_quantity,
        accepted_quantity: item.accepted_quantity,
        rejected_quantity: item.received_quantity - item.accepted_quantity,
        expiry_date: item.expiry_date,
      }));

      const totalAccepted = items.reduce((sum, item) => sum + item.accepted_quantity, 0);
      const totalRejected = items.reduce((sum, item) => sum + (item.rejected_quantity || 0), 0);
      const qualityStatus =
        totalRejected === 0 ? 'accepted' : totalAccepted === 0 ? 'rejected' : 'partial';

      const result = await createGRN({
        po_id: purchaseOrder.id,
        grn_number: grnNumber,
        received_date: receivedDate,
        received_by: receivedBy,
        quality_status: qualityStatus,
        remarks: qualityNotes || undefined,
        items,
      });

      dispatch(addGRN(result as never));
      dispatch(
        updatePurchaseOrder({
          ...purchaseOrder,
          status: 'delivered',
          actual_delivery_date: receivedDate,
        } as never)
      );
      onSuccess(result as GRN);
      navigate(`/grns/${(result as GRN).id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create GRN');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (deliveryPattern === 'accept-all') {
      await handleAcceptAllSubmit();
    } else if (deliveryPattern === 'simple') {
      await handleSimpleSubmit();
    } else {
      await handleDetailedSubmit();
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={`Create GRN for PO #${purchaseOrder.po_number}`}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-error-50 border border-error-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Icon name="CircleAlert" size={20} className="text-error-600 mt-0.5" />
              <p className="text-error-700">{error}</p>
            </div>
          </div>
        )}

        {/* GRN Header */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-neutral-900">GRN Information</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                GRN Number (from vendor) *
              </label>
              <Input
                type="text"
                value={grnNumber}
                onChange={(e) => setGrnNumber(e.target.value)}
                placeholder="Enter GRN number from vendor document"
                disabled={isBusy}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Received Date *
              </label>
              <Input
                type="date"
                value={receivedDate}
                onChange={(e) => setReceivedDate(e.target.value)}
                disabled={isBusy}
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Received By *
              </label>
              <Input
                type="text"
                value={receivedBy}
                onChange={(e) => setReceivedBy(e.target.value)}
                placeholder="Name of person receiving goods"
                disabled={isBusy}
                required
              />
            </div>
          </div>
        </div>

        {/* Delivery Pattern Selection */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-neutral-900">Delivery Pattern</h3>
          <p className="text-sm text-neutral-600">
            Choose how you want to record the received items
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              type="button"
              onClick={() => setDeliveryPattern('accept-all')}
              className={`p-4 rounded-lg border-2 transition-all ${
                deliveryPattern === 'accept-all'
                  ? 'border-primary-600 bg-primary-50'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
              disabled={isBusy}
            >
              <Icon
                name="CircleCheck"
                size={24}
                className={`mx-auto mb-2 ${
                  deliveryPattern === 'accept-all' ? 'text-primary-600' : 'text-neutral-400'
                }`}
              />
              <p className="font-semibold text-neutral-900">Accept All</p>
              <p className="text-xs text-neutral-600 mt-1">One-click, all items accepted</p>
            </button>

            <button
              type="button"
              onClick={() => setDeliveryPattern('simple')}
              className={`p-4 rounded-lg border-2 transition-all ${
                deliveryPattern === 'simple'
                  ? 'border-primary-600 bg-primary-50'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
              disabled={isBusy}
            >
              <Icon
                name="List"
                size={24}
                className={`mx-auto mb-2 ${
                  deliveryPattern === 'simple' ? 'text-primary-600' : 'text-neutral-400'
                }`}
              />
              <p className="font-semibold text-neutral-900">Simple Accept/Reject</p>
              <p className="text-xs text-neutral-600 mt-1">Yes/No per item</p>
            </button>

            <button
              type="button"
              onClick={() => setDeliveryPattern('detailed')}
              className={`p-4 rounded-lg border-2 transition-all ${
                deliveryPattern === 'detailed'
                  ? 'border-primary-600 bg-primary-50'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
              disabled={isBusy}
            >
              <Icon
                name="ClipboardList"
                size={24}
                className={`mx-auto mb-2 ${
                  deliveryPattern === 'detailed' ? 'text-primary-600' : 'text-neutral-400'
                }`}
              />
              <p className="font-semibold text-neutral-900">Detailed Quantities</p>
              <p className="text-xs text-neutral-600 mt-1">Specify received/accepted amounts</p>
            </button>
          </div>
        </div>

        {/* Item-Level Data Entry */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-neutral-900">Items</h3>

          {deliveryPattern === 'accept-all' && (
            <div className="bg-success-50 border border-success-200 rounded-lg p-6">
              <div className="flex items-start gap-3 mb-4">
                <Icon name="CircleCheck" size={24} className="text-success-600" />
                <div>
                  <p className="font-semibold text-success-900">Accept All Items</p>
                  <p className="text-sm text-success-700 mt-1">
                    All {purchaseOrder.items.length} items will be accepted with the default expiry
                    date
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Default Expiry Date *
                </label>
                <Input
                  type="date"
                  value={defaultExpiryDate}
                  onChange={(e) => setDefaultExpiryDate(e.target.value)}
                  disabled={isBusy}
                  required
                />
                <p className="text-xs text-neutral-600 mt-1">
                  This expiry date will be applied to all items
                </p>
              </div>

              <div className="mt-4 max-h-60 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-100 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3">Product</th>
                      <th className="text-right py-2 px-3">Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseOrder.items.map((item) => (
                      <tr key={item.id} className="border-b border-neutral-200">
                        <td className="py-2 px-3">{item.product_name}</td>
                        <td className="py-2 px-3 text-right">{item.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {deliveryPattern === 'simple' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-100">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                      Product
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">
                      Quantity
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">
                      Accept?
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                      Expiry Date
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                      Rejection Reason
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseOrder.items.map((poItem, index) => {
                    const simpleItem = simpleItems[index];
                    return (
                      <tr key={poItem.id} className="border-b border-neutral-200">
                        <td className="py-3 px-4">
                          <p className="font-medium text-neutral-900">{poItem.product_name}</p>
                        </td>
                        <td className="py-3 px-4 text-center">{poItem.quantity}</td>
                        <td className="py-3 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={simpleItem.accept}
                            onChange={(e) => {
                              const newItems = [...simpleItems];
                              newItems[index].accept = e.target.checked;
                              setSimpleItems(newItems);
                            }}
                            className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                            disabled={isBusy}
                          />
                        </td>
                        <td className="py-3 px-4">
                          {simpleItem.accept && (
                            <Input
                              type="date"
                              value={simpleItem.expiry_date}
                              onChange={(e) => {
                                const newItems = [...simpleItems];
                                newItems[index].expiry_date = e.target.value;
                                setSimpleItems(newItems);
                              }}
                              disabled={isBusy}
                              required
                            />
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {!simpleItem.accept && (
                            <Input
                              type="text"
                              value={simpleItem.rejection_reason}
                              onChange={(e) => {
                                const newItems = [...simpleItems];
                                newItems[index].rejection_reason = e.target.value;
                                setSimpleItems(newItems);
                              }}
                              placeholder="Enter reason"
                              disabled={isBusy}
                              required
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {deliveryPattern === 'detailed' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-100">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                      Product
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">
                      Ordered
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">
                      Received
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">
                      Accepted
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                      Expiry Date
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                      Rejection Reason
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseOrder.items.map((poItem, index) => {
                    const detailedItem = detailedItems[index];
                    const rejectedQty =
                      detailedItem.received_quantity - detailedItem.accepted_quantity;
                    return (
                      <tr key={poItem.id} className="border-b border-neutral-200">
                        <td className="py-3 px-4">
                          <p className="font-medium text-neutral-900">{poItem.product_name}</p>
                        </td>
                        <td className="py-3 px-4 text-center">{poItem.quantity}</td>
                        <td className="py-3 px-4">
                          <Input
                            type="number"
                            value={detailedItem.received_quantity}
                            onChange={(e) => {
                              const newItems = [...detailedItems];
                              newItems[index].received_quantity = parseInt(e.target.value) || 0;
                              if (
                                newItems[index].accepted_quantity >
                                newItems[index].received_quantity
                              ) {
                                newItems[index].accepted_quantity =
                                  newItems[index].received_quantity;
                              }
                              setDetailedItems(newItems);
                            }}
                            min={0}
                            max={poItem.quantity}
                            disabled={isBusy}
                            className="w-24 text-center"
                            required
                          />
                        </td>
                        <td className="py-3 px-4">
                          <Input
                            type="number"
                            value={detailedItem.accepted_quantity}
                            onChange={(e) => {
                              const newItems = [...detailedItems];
                              newItems[index].accepted_quantity = parseInt(e.target.value) || 0;
                              setDetailedItems(newItems);
                            }}
                            min={0}
                            max={detailedItem.received_quantity}
                            disabled={isBusy}
                            className="w-24 text-center"
                            required
                          />
                        </td>
                        <td className="py-3 px-4">
                          {detailedItem.accepted_quantity > 0 && (
                            <Input
                              type="date"
                              value={detailedItem.expiry_date}
                              onChange={(e) => {
                                const newItems = [...detailedItems];
                                newItems[index].expiry_date = e.target.value;
                                setDetailedItems(newItems);
                              }}
                              disabled={isBusy}
                              required
                            />
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {rejectedQty > 0 && (
                            <Input
                              type="text"
                              value={detailedItem.rejection_reason}
                              onChange={(e) => {
                                const newItems = [...detailedItems];
                                newItems[index].rejection_reason = e.target.value;
                                setDetailedItems(newItems);
                              }}
                              placeholder="Enter reason"
                              disabled={isBusy}
                              required
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Quality Inspection */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-neutral-900">Quality Inspection (Optional)</h3>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Quality Notes</label>
            <textarea
              value={qualityNotes}
              onChange={(e) => setQualityNotes(e.target.value)}
              placeholder="Enter any quality inspection notes, observations, or remarks..."
              rows={4}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              disabled={isBusy}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t border-neutral-200">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isBusy} className="flex-1">
            {isBusy ? (
              <>
                <Icon name="Loader" size={20} className="mr-2 animate-spin" />
                Creating GRN...
              </>
            ) : (
              <>
                <Icon name="Check" size={20} className="mr-2" />
                Create GRN
              </>
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
